import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { routes } from '../config/routes';
import type { MetaConfig } from '../types';

export function useRouteMetaTags() {
  const location = useLocation();

  useEffect(() => {
    const route = routes.find(r => r.path === location.pathname);
    if (!route) return;

    const { meta } = route;
    updateMetaTags(meta);
  }, [location.pathname]);
}

function updateMetaTags(meta: MetaConfig) {
  // Update title
  document.title = meta.title;

  // Update meta tags
  const metaTags = {
    description: meta.description,
    keywords: Array.isArray(meta.keywords) ? meta.keywords.join(', ') : meta.keywords,
    'og:title': meta.title,
    'og:description': meta.description,
    'og:url': meta.canonicalUrl,
    'twitter:title': meta.title,
    'twitter:description': meta.description
  };

  Object.entries(metaTags).forEach(([name, content]) => {
    let tag = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  });

  // Update canonical link
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', meta.canonicalUrl);

  // Update structured data
  if (meta.structuredData) {
    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(meta.structuredData);
  }
}